import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { GraduationCap, CheckCircle2, ChevronDown, ChevronUp, Clock } from "lucide-react";
import { AcademyCertificateRow } from "@/components/sales/AcademyCertificate";

export default function SalesAcademy({ onboarding, onComplete, busy, title = "Sales Academy — Mandatory Onboarding", subtitle = "Complete all steps to unlock the lead queue", testid = "sales-academy", certificatePath = "", certificateName = "specpixel-academy-certificate.pdf" }) {
  const [open, setOpen] = useState(null);
  const done = new Set(onboarding.completed);
  const pct = Math.round((onboarding.completed.length / onboarding.steps.length) * 100);

  return (
    <div data-testid={testid} className="border border-nc-blue/40 bg-nc-blue/5">
      <div className="border-b border-nc-blue/20 px-5 py-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <GraduationCap className="w-5 h-5 text-nc-blue" strokeWidth={1.75} />
          <div>
            <p className="font-sans font-bold uppercase text-sm text-zinc-100">{title}</p>
            <p className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 uppercase mt-0.5">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-40 h-1.5 bg-white/10">
            <div data-testid="academy-progress" className="h-full bg-nc-blue transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
          <span className="font-mono text-[10px] text-nc-blue">{onboarding.completed.length}/{onboarding.steps.length}</span>
        </div>
      </div>
      <div>
        {onboarding.steps.map((s, i) => {
          const complete = done.has(s.id);
          const isOpen = open === s.id;
          return (
            <div key={s.id} data-testid={`academy-step-${s.id}`} className={`border-b border-white/5 ${complete ? "opacity-75" : ""}`}>
              <button
                data-testid={`academy-toggle-${s.id}`}
                onClick={() => setOpen(isOpen ? null : s.id)}
                className="w-full flex items-center gap-4 px-5 py-3.5 text-left hover:bg-white/5 transition-colors"
              >
                <span className="font-mono text-[10px] text-zinc-600 w-6">{String(i + 1).padStart(2, "0")}</span>
                {complete ? <CheckCircle2 className="w-4 h-4 text-nc-green shrink-0" /> : <span className="w-4 h-4 border border-zinc-600 shrink-0" />}
                <span className="font-sans font-semibold text-sm text-zinc-100 flex-1">{s.title}</span>
                <span className="font-mono text-[9px] text-zinc-600 flex items-center gap-1"><Clock className="w-3 h-3" /> {s.minutes} min</span>
                {isOpen ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
              </button>
              {isOpen && (
                <div className="px-5 pb-5 pl-16">
                  <div className="prose prose-invert prose-sm max-w-none font-mono text-xs text-zinc-400 leading-relaxed prose-headings:font-sans prose-headings:text-zinc-100 prose-headings:uppercase prose-headings:text-sm prose-strong:text-zinc-200 prose-li:marker:text-nc-blue">
                    <ReactMarkdown>{s.body}</ReactMarkdown>
                  </div>
                  {!complete && (
                    <button
                      data-testid={`academy-complete-${s.id}`}
                      disabled={busy}
                      onClick={() => onComplete(s.id)}
                      className="mt-4 font-mono text-[10px] tracking-[0.2em] uppercase bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white px-4 py-2.5 transition-colors"
                    >
                      Mark as Complete
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {onboarding.done && certificatePath && (
        <AcademyCertificateRow path={certificatePath} filename={certificateName} />
      )}
    </div>
  );
}
